import { BackendFilterPayload } from '../server/properties/propertyTypes';
import { parseCurrency } from '../utils/formatters';

export interface BrokerFilterHookInput {
  propertyCodeFilter?: string;
  ubicacionFilter?: string[];
  propertyTypeFilter?: string[];
  estrategiaComercialFilter?: string[];
  ownerFilter?: string[];
  roomsFilter?: number[];
  bathsFilter?: number[];
  garagesFilter?: number[];
  priceFilter?: { min: number | string | null; max: number | string | null };
  areaFilter?: { min: number | string | null; max: number | string | null };
  estratoFilter?: number[];
  antiquityFilter?: { min: number | string | null; max: number | string | null };
  withElevatorFilter?: boolean;
  onlyRecentFilter?: boolean;
  spatialFilter?: any;
  searchMode?: 'ai' | 'code' | 'map';
  appUser?: any;
  currentPage?: number;
  pageSize?: number;
  sortConfigs?: any;
  customSortOrders?: any;
  newlyAddedRange?: any;
  viewMode?: any;
  isSelectionMode?: any;
  inmobiliariasConfig?: any;
}

function parseLocationString(loc: string): { city?: string; localidad?: string; neighborhood?: string } {
  const upper = loc.toUpperCase().trim();
  if (upper.startsWith('BOGOTÁ, D.C.') || upper.startsWith('BOGOTA, D.C.')) {
    const remainder = loc.slice(upper.startsWith('BOGOTÁ, D.C.') ? 12 : 12).trim();
    const subParts = remainder ? remainder.split(',').map((p) => p.trim()).filter(Boolean) : [];
    return {
      city: 'BOGOTÁ, D.C.',
      localidad: subParts[0] || undefined,
      neighborhood: subParts[1] || undefined,
    };
  }

  const parts = loc.split(',').map((p) => p.trim()).filter(Boolean);
  return {
    city: parts[0] || undefined,
    localidad: parts[1] || undefined,
    neighborhood: parts[2] || undefined,
  };
}

/**
 * Convierte los estados y filtros del dashboard en el payload tipado del Backend
 */
export function buildBackendFilterPayload(input: BrokerFilterHookInput, page = 1, pageSize = 20): BackendFilterPayload {
  const {
    propertyCodeFilter = '',
    ubicacionFilter = [],
    propertyTypeFilter = [],
    estrategiaComercialFilter = [],
    ownerFilter = [],
    roomsFilter = [],
    bathsFilter = [],
    garagesFilter = [],
    priceFilter = { min: null, max: null },
    areaFilter = { min: null, max: null },
    estratoFilter = [],
    withElevatorFilter = false,
    onlyRecentFilter = false,
    spatialFilter = null,
    searchMode = 'ai',
    appUser,
    sortConfigs = [],
  } = input;

  const minPrice = typeof priceFilter.min === 'string' ? parseCurrency(priceFilter.min) : priceFilter.min;
  const maxPrice = typeof priceFilter.max === 'string' ? parseCurrency(priceFilter.max) : priceFilter.max;
  const minArea = areaFilter.min != null && areaFilter.min !== '' ? Number(areaFilter.min) : null;
  const maxArea = areaFilter.max != null && areaFilter.max !== '' ? Number(areaFilter.max) : null;

  const primarySort = Array.isArray(sortConfigs) && sortConfigs.length > 0 ? sortConfigs[0] : null;
  const sortBy = primarySort?.key === 'price' ? 'price' : primarySort?.key === 'area' ? 'area' : 'fecha_desc';
  const sortOrder = primarySort?.direction === 'asc' ? 'asc' : 'desc';

  const cities: string[] = [];
  const localidades: string[] = [];
  const neighborhoods: string[] = [];

  for (const loc of ubicacionFilter) {
    const parsed = parseLocationString(loc);
    if (parsed.city) cities.push(parsed.city);
    if (parsed.localidad) localidades.push(parsed.localidad);
    if (parsed.neighborhood) neighborhoods.push(parsed.neighborhood);
  }

  return {
    searchMode,
    keyword: searchMode === 'ai' ? propertyCodeFilter : undefined,
    propertyCodes: searchMode === 'code' ? [propertyCodeFilter] : undefined,
    cities: cities.length > 0 ? Array.from(new Set(cities)) : undefined,
    localidades: localidades.length > 0 ? Array.from(new Set(localidades)) : undefined,
    neighborhoods: neighborhoods.length > 0 ? Array.from(new Set(neighborhoods)) : undefined,
    propertyTypes: propertyTypeFilter.length > 0 ? propertyTypeFilter : undefined,
    owners: ownerFilter.length > 0 ? ownerFilter : undefined,
    minRooms: roomsFilter.length > 0 ? Math.min(...roomsFilter) : undefined,
    maxRooms: roomsFilter.length > 0 ? Math.max(...roomsFilter) : undefined,
    minBathrooms: bathsFilter.length > 0 ? Math.min(...bathsFilter) : undefined,
    maxBathrooms: bathsFilter.length > 0 ? Math.max(...bathsFilter) : undefined,
    minParkings: garagesFilter.length > 0 ? Math.min(...garagesFilter) : undefined,
    estratos: estratoFilter.length > 0 ? estratoFilter : undefined,
    commercialStrategies: estrategiaComercialFilter.length > 0 ? estrategiaComercialFilter : undefined,
    minPrice,
    maxPrice,
    minArea,
    maxArea,
    hasElevator: withElevatorFilter ? true : undefined,
    onlyRecent: onlyRecentFilter ? true : undefined,
    spatialFilter: spatialFilter || undefined,
    role: appUser?.role,
    sortBy,
    sortOrder,
    page,
    pageSize,
  };
}
